import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { z } from "zod";
import foundryLogo from "@/assets/foundry-logo.png";

// Input validation schema
const authSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .max(72, { message: "Password must be less than 72 characters" })
    .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
    .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
    .regex(/[0-9]/, { message: "Password must contain at least one number" }),
});

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR-safe
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/");
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/auth`,
      });
      
      if (error) throw error;
      toast.success("Password reset email sent! Check your inbox.");
      setIsResetMode(false);
    } catch (error: any) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    const validation = authSchema.safeParse({ 
      email: email.trim(), 
      password 
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setLoading(true);

    try {
      const trimmedEmail = email.trim();
      
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        });

        if (error) throw error;
        toast.success("Welcome back!");
      } else {
        const redirectUrl = `${window.location.origin}/`;
        const { error } = await supabase.auth.signUp({
          email: trimmedEmail,
          password,
          options: {
            emailRedirectTo: redirectUrl,
          },
        });

        if (error) throw error;
        toast.success("Account created! You can now log in.");
      }
    } catch (error: any) {
      // Security: Don't log sensitive auth details
      if (error.message.includes("Invalid login credentials")) {
        toast.error("Invalid email or password");
      } else if (error.message.includes("User already registered")) {
        toast.error("This email is already registered. Please log in.");
      } else if (error.message.includes("Password should be at least")) {
        toast.error("Password does not meet security requirements");
      } else {
        toast.error("Authentication failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="text-center mb-8">
          <img 
            src={foundryLogo} 
            alt="Foundry Cabinets" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-light text-slate-800 mb-2">
            {isResetMode ? "Reset Password" : (isLogin ? "Welcome Back" : "Create Account")}
          </h1>
          <p className="text-slate-600">
            {isResetMode 
              ? "Enter your email to receive a reset link" 
              : (isLogin ? "Sign in to access the proposal builder" : "Sign up to get started")}
          </p>
        </div>

        <form onSubmit={isResetMode ? handlePasswordReset : handleAuth} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {!isResetMode && (
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                minLength={8}
              />
              <p className="text-xs text-slate-500 mt-1">
                {isLogin 
                  ? "Enter your password" 
                  : "Min 8 characters, with uppercase, lowercase, and number"}
              </p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Please wait..." : (isResetMode ? "Send Reset Link" : (isLogin ? "Sign In" : "Sign Up"))}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {!isResetMode && (
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="block w-full text-sm text-blue-600 hover:text-blue-700"
            >
              {isLogin ? "Need an account? Sign up" : "Already have an account? Sign in"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsResetMode(!isResetMode)}
            className="block w-full text-sm text-blue-600 hover:text-blue-700"
          >
            {isResetMode ? "Back to sign in" : "Forgot password?"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
